import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import { ID_CARD_FILL_ICON, CLOSE_NEW_ICON } from '@Images';
import { StepHeader, CommonButton, ModalFactory } from '@Components';
import { screenSettings, totalSteps, RE_KYC_SCREEN } from '../../utils/constants';
import { NF_SCREENS } from '../../root/screenConstants';
import { Col, FormContainer, ParentContainer, Row } from '../../styles';
import {
  Container,
  SubContainer,
  Labels,
  Value,
  NameValuePair,
  RemitterDetailsHeading,
  AddressContainer
} from './style';
import CancelPaymentModal from '../../utils/cancel-payment-modal';
import { getUserProfileData } from '@Redux/actions/user-state';
import {
  changeNiumForexScreen,
  storeCafDocumentChanged,
  initiateNiumForexTransaction
} from '@Redux/actions/nium-forex';
import { NF_REUSE_KYC_EVENT, NF_REDO_KYC_EVENT } from '@Utils/constants/eventsConstants';
import { FLOW_TYPE } from '@Utils/constants';
import { maskPAN } from '../../utils/util';
import { IReKycScreen } from '../../utils/interface';
import { IStore } from '../../../../store.type';
import { logEvent } from '../../../../utils/events/logUserEvents';
import { LOG_TYPE } from '../../../../utils/events/eventsConstants';

interface IState {
  aadhaarName: string;
  panName: string;
  address: string;
  panNumber: string;
  aadhaarNumber: string;
  dateOfBirth: string;
}

const ReKycScreen: React.FC<IReKycScreen> = (props) => {
  const {
    activateLoader,
    disableLoader,
    getUserProfileData,
    changeNiumForexScreen,
    storeCafDocumentChanged,
    initiateNiumForexTransaction,
    tempTransactionId
  } = props;
  const stepperData = screenSettings[NF_SCREENS.re_kyc as keyof typeof screenSettings];

  const [state, setState] = useState<IState>({
    aadhaarName: '',
    panName: '',
    address: '',
    panNumber: '',
    aadhaarNumber: '',
    dateOfBirth: ''
  });

  const onCancelReKyc = async () => {
    // @ts-ignore
    await ModalFactory.builder?.(
      (onClose: any) => (
        <CancelPaymentModal
          onContinue={() => {
            onClose(true);
          }}
          onConfirm={() => {
            onClose(true);
            changeNiumForexScreen(NF_SCREENS.send_money);
          }}
        />
      ),
      'cancel-re-kyc center-modal insta-modal',
      true
    );
  };

  const handleLogReDoKycEvents = () => {
    const ctData = {
      pageName: NF_SCREENS.re_kyc
    };
    logEvent(NF_REDO_KYC_EVENT, FLOW_TYPE.TRANSACTION, ctData, LOG_TYPE.CLEVER_TAP);
  };

  const handleLogReUseKycEvents = () => {
    const ctData = {
      pageName: NF_SCREENS.re_kyc
    };
    logEvent(NF_REUSE_KYC_EVENT, FLOW_TYPE.TRANSACTION, ctData, LOG_TYPE.CLEVER_TAP);
  };

  const handleNoDetailsChanged = async () => {
    activateLoader();
    const res = await initiateNiumForexTransaction(tempTransactionId);
    if (res?.success) {
      handleLogReUseKycEvents();
      changeNiumForexScreen(NF_SCREENS.select_bank);
    }
    disableLoader();
  };

  const handleDetailsChanged = async () => {
    handleLogReDoKycEvents();
    await storeCafDocumentChanged(true);
    changeNiumForexScreen(NF_SCREENS.aadhar_verification);
  };

  const handleRetrieveUserInfo = async () => {
    const response = await getUserProfileData(true);
    const remitterDetails: any = response?.data?.data?.remitterDetails;
    if (remitterDetails) {
      setState({
        aadhaarName: remitterDetails?.name_as_per_aadhar,
        panName: remitterDetails?.pan_name,
        address: remitterDetails?.address1,
        dateOfBirth: remitterDetails?.date_of_birth,
        panNumber: remitterDetails?.pan_card ? maskPAN(remitterDetails?.pan_card) : '',
        aadhaarNumber: remitterDetails?.aadhar_card
      });
    }

    disableLoader();
  };

  useEffect(() => {
    activateLoader();
    handleRetrieveUserInfo();
    //eslint-disable-next-line
  }, []);

  return (
    <ParentContainer padding={[30, 0, 48, 0]}>
      <div className="navigationContainer">
        <img className="closeButton" src={CLOSE_NEW_ICON} alt="close" onClick={onCancelReKyc} />
      </div>
      <StepHeader
        heading={RE_KYC_SCREEN.heading}
        headerLogo={ID_CARD_FILL_ICON}
        stepNumber={stepperData?.stepNumber}
        totalSteps={totalSteps}
        subHeading={RE_KYC_SCREEN?.subHeading}
      />
      <FormContainer marginTop={45}>
        <SubContainer>
          <Row padding={[13, 0]}>
            <Col width={450}>
              <Container>
                <RemitterDetailsHeading>{RE_KYC_SCREEN?.remitterDetails}</RemitterDetailsHeading>
                <NameValuePair>
                  <Labels>{RE_KYC_SCREEN?.remitterAadhaarName}</Labels>
                  <Value>{state?.aadhaarName}</Value>
                </NameValuePair>
                <NameValuePair>
                  <Labels>{RE_KYC_SCREEN?.remitterPanName}</Labels>
                  <Value>{state?.panName}</Value>
                </NameValuePair>
                <NameValuePair>
                  <Labels>{RE_KYC_SCREEN?.remitteAadhaarNumber}</Labels>
                  <Value>{state?.aadhaarNumber}</Value>
                </NameValuePair>
                <NameValuePair>
                  <Labels>{RE_KYC_SCREEN?.remittePanNumber}</Labels>
                  <Value>{state?.panNumber}</Value>
                </NameValuePair>
                <NameValuePair>
                  <Labels>{RE_KYC_SCREEN?.remitteDob}</Labels>
                  <Value>{state?.dateOfBirth}</Value>
                </NameValuePair>
                <AddressContainer>
                  <span className="addressLabel">{RE_KYC_SCREEN?.remitterAddress}</span>
                  <span className="addressValue">{state?.address}</span>
                </AddressContainer>
              </Container>
            </Col>
          </Row>
          <Row padding={[24, 0, 0, 0]}>
            <Col width={450}>
              <CommonButton
                type="primary"
                text={RE_KYC_SCREEN.primarySubmitBtn}
                onClick={handleNoDetailsChanged}
                width={450}
                height={46}
              />
            </Col>
          </Row>
          <Row padding={[24, 0, 0, 0]}>
            <Col width={450}>
              <CommonButton
                type="secondary"
                onClick={handleDetailsChanged}
                text={RE_KYC_SCREEN.secondrySubmitBtn}
                width={450}
                height={46}
              />
            </Col>
          </Row>
        </SubContainer>
      </FormContainer>
    </ParentContainer>
  );
};
function mapStateToProps(reduxState: IStore) {
  const {
    rootReducer: {
      niumForexReducer: {
        transactionDetails: { tempTransactionId }
      }
    }
  } = reduxState;
  return {
    tempTransactionId
  };
}

export default connect(mapStateToProps, {
  activateLoader,
  disableLoader,
  getUserProfileData,
  changeNiumForexScreen,
  storeCafDocumentChanged,
  initiateNiumForexTransaction
  // @ts-ignore
})(ReKycScreen);
